import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CameraIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import appleIcon from '../assets/app-icons/apple.svg';
import cursorIcon from '../assets/app-icons/cursor.svg';
import figmaIcon from '../assets/app-icons/figma.svg';
import firefoxIcon from '../assets/app-icons/firefox.svg';
import githubIcon from '../assets/app-icons/github.svg';
import githubDesktopIcon from '../assets/app-icons/githubdesktop.svg';
import googleChromeIcon from '../assets/app-icons/googlechrome.svg';
import itermIcon from '../assets/app-icons/iterm2.svg';
import microsoftEdgeIcon from '../assets/app-icons/microsoftedge.svg';
import microsoftExcelIcon from '../assets/app-icons/microsoftexcel.svg';
import microsoftOutlookIcon from '../assets/app-icons/microsoftoutlook.svg';
import microsoftPowerPointIcon from '../assets/app-icons/microsoftpowerpoint.svg';
import microsoftTeamsIcon from '../assets/app-icons/microsoftteams.svg';
import microsoftWordIcon from '../assets/app-icons/microsoftword.svg';
import notionIcon from '../assets/app-icons/notion.svg';
import safariIcon from '../assets/app-icons/safari.svg';
import slackIcon from '../assets/app-icons/slack.svg';
import spotifyIcon from '../assets/app-icons/spotify.svg';
import visualStudioCodeIcon from '../assets/app-icons/visualstudiocode.svg';
import whatsappIcon from '../assets/app-icons/whatsapp.svg';
import windowsTerminalIcon from '../assets/app-icons/windowsterminal.svg';
import zoomIcon from '../assets/app-icons/zoom.svg';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import { DEFAULT_WORKING_HOURS, WorkingHoursFields, describeWorkingHours, normalizeWorkingHours, workingHoursProblem } from '../discovery/workingHours';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const LOCAL_SETTINGS_URL = 'http://127.0.0.1:47232/';
const PAGE_SIZE = 40;

async function saveRuling(sessionId, agentId, eventId, label, note) {
  const response = await axios.post(
    `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/rulings`,
    { agentId, eventId, label, note },
  );
  return response.data.data;
}

async function forgetRuling(sessionId, rulingId) {
  await axios.delete(`${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/rulings/${rulingId}`);
}

const statusClass = {
  on: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  stopped: 'bg-gray-100 text-gray-700',
};

const toneClass = {
  healthy: 'bg-green-50 text-green-800 ring-1 ring-inset ring-green-600/15',
  quiet: 'bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-600/15',
  offline: 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-600/15',
  paused: 'bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-600/15',
  stopped: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-500/10',
};

const kindLabel = {
  uploaded: 'Uploaded a card',
  dropped: 'Dropped a window',
  unread: 'Could not read the window',
};

// PostHog-style severity for each retained activity kind.
const levelMeta = {
  uploaded: { level: 'info', label: 'Info', dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', bar: 'bg-emerald-500' },
  dropped: { level: 'warn', label: 'Warn', dot: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 ring-amber-600/25', bar: 'bg-amber-400' },
  unread: { level: 'error', label: 'Error', dot: 'bg-red-500', badge: 'bg-red-50 text-red-700 ring-red-600/20', bar: 'bg-red-500' },
};

// Real brand icons (official Simple Icons glyphs, bundled locally), matched
// by application name. Order matters: more specific names come first.
const appStyles = [
  ['github desktop', githubDesktopIcon],
  ['google chrome', googleChromeIcon],
  ['chrome', googleChromeIcon],
  ['microsoft edge', microsoftEdgeIcon],
  ['edge', microsoftEdgeIcon],
  ['safari', safariIcon],
  ['firefox', firefoxIcon],
  ['outlook', microsoftOutlookIcon],
  ['excel', microsoftExcelIcon],
  ['microsoft word', microsoftWordIcon],
  ['powerpoint', microsoftPowerPointIcon],
  ['whatsapp', whatsappIcon],
  ['slack', slackIcon],
  ['microsoft teams', microsoftTeamsIcon],
  ['teams', microsoftTeamsIcon],
  ['zoom', zoomIcon],
  ['spotify', spotifyIcon],
  ['github', githubIcon],
  ['cursor', cursorIcon],
  ['visual studio', visualStudioCodeIcon],
  ['vs code', visualStudioCodeIcon],
  ['figma', figmaIcon],
  ['notion', notionIcon],
  ['iterm', itermIcon],
  ['terminal', windowsTerminalIcon],
  ['powershell', windowsTerminalIcon],
  ['system settings', appleIcon],
  ['finder', appleIcon],
  ['preview', appleIcon],
];

const tilePalette = ['#0ea5e9', '#8b5cf6', '#ec4899', '#f97316', '#10b981', '#6366f1', '#14b8a6', '#e11d48'];

function appMeta(app) {
  const name = String(app || '').trim();
  const lower = name.toLowerCase();
  const match = appStyles.find(([needle]) => lower.includes(needle));
  if (match) return { icon: match[1], name: name || 'Unknown app' };
  let hash = 0;
  for (let index = 0; index < lower.length; index += 1) hash = (hash * 31 + lower.charCodeAt(index)) >>> 0;
  return { icon: null, color: tilePalette[hash % tilePalette.length], name: name || 'Unknown app', initial: (name || '?').slice(0, 1).toUpperCase() };
}

/**
 * Structured view of one event. The discovery master now sends these fields
 * directly; the fallback parses the retained `detail` line so older payloads
 * render the same way.
 */
function describeEvent(event) {
  if (!event) return { level: 'info', headline: '', message: '', attributes: [] };
  const headline = kindLabel[event.kind] || event.kind || 'Event';
  if (event.message) {
    return {
      level: event.level || levelMeta[event.kind]?.level || 'info',
      headline,
      message: event.message,
      summary: event.summary || '',
      window: event.window || '',
      screen: event.screen || '',
      action: event.action || '',
      changed: event.changed || '',
      medium: event.medium || '',
      durationLabel: event.durationLabel || '',
      reason: event.reason || '',
      verdict: event.verdict || '',
      confidence: event.confidence ?? null,
      attributes: Array.isArray(event.attributes) ? event.attributes : [],
    };
  }
  const detail = String(event.detail || '').trim();
  const parsed = { summary: '', window: '', screen: '', action: '', changed: '', medium: '', durationLabel: '', reason: '', verdict: '', confidence: null };
  if (event.kind === 'uploaded') {
    const actions = [];
    for (const part of detail.split(' · ').map((piece) => piece.trim()).filter(Boolean)) {
      if (part.startsWith('Window: ')) parsed.window = part.slice(8);
      else if (part.startsWith('Screen: ')) parsed.screen = part.slice(8);
      else if (part.startsWith('Changed: ')) parsed.changed = part.slice(9);
      else if (part === 'Recorded as a still') parsed.medium = 'still';
      else if (part === 'Recorded as a clip') parsed.medium = 'clip';
      else if (/^(?:About )?\d+\s*(?:s|min) on this window$/.test(part)) parsed.durationLabel = part.replace(/ on this window$/, '');
      else if (!parsed.summary) parsed.summary = part;
      else actions.push(part);
    }
    parsed.action = actions.join(' · ');
  } else if (event.kind === 'dropped') {
    const windowMatch = detail.match(/^Window “([^”]+)”\s*—\s*(.*)$/);
    if (windowMatch) { parsed.window = windowMatch[1].trim(); parsed.reason = windowMatch[2].trim(); } else parsed.reason = detail;
    const confidenceMatch = parsed.reason.match(/(\d{1,3})%\s*sure/);
    if (confidenceMatch) parsed.confidence = Math.min(100, Number(confidenceMatch[1]));
    if (/excluded list/i.test(parsed.reason)) parsed.verdict = 'excluded';
    else if (/personal use|leaned personal/i.test(parsed.reason)) parsed.verdict = 'personal';
    else if (/leaned professional/i.test(parsed.reason)) parsed.verdict = 'professional';
    else if (/could not classify/i.test(parsed.reason)) parsed.verdict = 'unclassified';
  } else {
    parsed.reason = detail;
  }
  const message = event.kind === 'uploaded'
    ? parsed.summary || parsed.action || parsed.window || headline
    : event.kind === 'dropped'
      ? (parsed.window ? `Window “${parsed.window}” was dropped` : 'Window was dropped')
      : parsed.reason || headline;
  const attributes = [
    ['summary', 'Summary', parsed.summary], ['window.title', 'Window', parsed.window], ['screen', 'Screen', parsed.screen],
    ['action', 'Action', parsed.action], ['changed', 'Changed', parsed.changed],
    ['medium', 'Recorded as', parsed.medium === 'still' ? 'Still image' : parsed.medium === 'clip' ? 'Clip' : ''],
    ['duration', 'Time on window', parsed.durationLabel], ['reason', 'Reason', parsed.reason],
    ['jev.verdict', 'Jev verdict', parsed.verdict], ['jev.confidence', 'Jev confidence', parsed.confidence === null ? '' : `${parsed.confidence}%`],
  ].filter(([, , value]) => value).map(([key, label, value]) => ({ key, label, value }));
  return { level: levelMeta[event.kind]?.level || 'info', headline, message, ...parsed, attributes };
}

const DiscoveryAgent = () => {
  const { sessionId, agentId } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [events, setEvents] = useState([]);
  const [apps, setApps] = useState([]);
  const [appIcons, setAppIcons] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [app, setApp] = useState('');
  const [live, setLive] = useState(true);
  const [freshIds, setFreshIds] = useState(() => new Set());
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [costLimit, setCostLimit] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);
  const limitDirtyRef = useRef(false);
  const [hoursMode, setHoursMode] = useState('session');
  const [hours, setHours] = useState(DEFAULT_WORKING_HOURS);
  const [savingHours, setSavingHours] = useState(false);
  const hoursDirtyRef = useRef(false);
  const [kindCounts, setKindCounts] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const requestRef = useRef(0);
  const tailRef = useRef(0);
  const hasAgentRef = useRef(false);
  const logRef = useRef(null);
  const eventsRef = useRef([]);
  const scrollOnLoadRef = useRef(false);
  const searchTouchedRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      if (searchTouchedRef.current) setPage(1);
      searchTouchedRef.current = true;
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), page: String(page) });
    if (search) params.set('q', search);
    if (kind) params.set('kind', kind);
    if (app) params.set('app', app);
    const response = await axios.get(
      `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/agents/${agentId}?${params}`,
    );
    if (requestId !== requestRef.current) return;
    const data = response.data.data;
    if (data?.agent) hasAgentRef.current = true;
    setPayload(data);
    setKindCounts(data?.kindCounts || null);
    setLoadError('');
    const nextEvents = data?.events || [];
    eventsRef.current = nextEvents;
    setEvents(nextEvents);
    setApps(data?.apps || []);
    if (data?.appIcons) setAppIcons(data.appIcons);
    setTotal(data?.page?.total || 0);
    setPages(data?.page?.pages || 1);
    if (scrollOnLoadRef.current && logRef.current) {
      scrollOnLoadRef.current = false;
      logRef.current.scrollIntoView({ block: 'start' });
    }
  }, [sessionId, agentId, page, search, kind, app]);

  useEffect(() => {
    let cancelled = false;
    load()
      .catch((error) => {
        console.error('Error fetching agent log:', error);
        if (cancelled) return;
        const message = error.response?.data?.error || 'Discovery master is unreachable';
        toast.error(message);
        const status = error.response?.status;
        if (!hasAgentRef.current && status !== 400 && status !== 404) setLoadError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const markFresh = useCallback((ids) => {
    setFreshIds((current) => {
      const next = new Set(current);
      for (const id of ids) next.add(id);
      return next;
    });
    window.setTimeout(() => {
      setFreshIds((current) => {
        const next = new Set(current);
        for (const id of ids) next.delete(id);
        return next;
      });
    }, 2500);
  }, []);

  const tail = useCallback(async (cursor) => {
    const requestId = tailRef.current + 1;
    tailRef.current = requestId;
    // load() bumps requestRef for a page, filter, or reload. A tail that
    // started before that must not merge its page-1 rows into the new view.
    const loadId = requestRef.current;
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), page: '1' });
    if (cursor) params.set('after', cursor);
    if (search) params.set('q', search);
    if (kind) params.set('kind', kind);
    if (app) params.set('app', app);
    const response = await axios.get(
      `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/agents/${agentId}?${params}`,
    );
    if (requestId !== tailRef.current || loadId !== requestRef.current) return;
    const data = response.data.data;
    if (data?.agent) hasAgentRef.current = true;
    setPayload(data);
    setKindCounts(data?.kindCounts || null);
    setLoadError('');
    setApps(data?.apps || []);
    if (data?.appIcons) setAppIcons(data.appIcons);
    setTotal(data?.page?.total || 0);
    setPages(data?.page?.pages || 1);
    const incoming = data?.events || [];
    if (!cursor) {
      eventsRef.current = incoming;
      setEvents(incoming);
      return;
    }
    const seen = new Set(eventsRef.current.map((event) => event.id));
    const added = incoming.filter((event) => event?.id && !seen.has(event.id));
    if (added.length === 0) return;
    markFresh(added.map((event) => event.id));
    const merged = [...added, ...eventsRef.current];
    merged.sort((a, b) => String(b.atIso || '').localeCompare(String(a.atIso || '')) || String(b.id).localeCompare(String(a.id)));
    const next = merged.slice(0, PAGE_SIZE);
    eventsRef.current = next;
    setEvents(next);
  }, [sessionId, agentId, search, kind, app, markFresh]);

  useEffect(() => {
    if (!live || page !== 1) return undefined;
    const timer = setInterval(() => {
      const newest = eventsRef.current[0];
      const cursor = newest?.atIso && newest?.id ? `${newest.atIso}|${newest.id}` : '';
      tail(cursor).catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [live, page, tail]);

  const goToPage = (next) => {
    const target = Math.min(pages, Math.max(1, next));
    if (target === page) return;
    if (target !== 1) setLive(false);
    scrollOnLoadRef.current = true;
    setPage(target);
  };

  const resumeLive = () => {
    setLive(true);
    if (page !== 1) {
      scrollOnLoadRef.current = true;
      setPage(1);
    }
  };

  const volume = useMemo(() => {
    const buckets = Array.from({ length: 28 }, () => ({ info: 0, warn: 0, error: 0, total: 0, at: null }));
    const times = events.map((event) => Date.parse(event.atIso || '')).filter((time) => Number.isFinite(time));
    if (times.length === 0) return { buckets, max: 0 };
    const newest = Math.max(...times);
    const oldest = Math.min(...times);
    const span = Math.max(60_000, newest - oldest);
    for (const event of events) {
      const time = Date.parse(event.atIso || '');
      if (!Number.isFinite(time)) continue;
      const slot = Math.min(buckets.length - 1, Math.floor(((time - oldest) / span) * buckets.length));
      const level = describeEvent(event).level;
      buckets[slot][level] += 1;
      buckets[slot].total += 1;
      buckets[slot].at = time;
    }
    return { buckets, max: Math.max(1, ...buckets.map((bucket) => bucket.total)) };
  }, [events]);

  const appCounts = useMemo(() => {
    const counts = new Map();
    for (const event of events) if (event.app) counts.set(event.app, (counts.get(event.app) || 0) + 1);
    return counts;
  }, [events]);

  useEffect(() => {
    if (limitDirtyRef.current) return;
    const raw = String(payload?.health?.costLimit || '').replace(/^\$/, '');
    setCostLimit(raw);
  }, [payload?.health?.costLimit]);

  useEffect(() => {
    if (hoursDirtyRef.current) return;
    if (payload?.agent?.workingHours) {
      setHoursMode('custom');
      setHours(normalizeWorkingHours(payload.agent.workingHours));
      return;
    }
    setHoursMode('session');
    setHours(normalizeWorkingHours(payload?.sessionWorkingHours));
  }, [payload?.agent?.workingHours, payload?.sessionWorkingHours]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const onKey = (keyEvent) => {
      if (keyEvent.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId]);

  const copyLocalSettings = async () => {
    try {
      await navigator.clipboard.writeText(LOCAL_SETTINGS_URL);
      toast.success('Local settings link copied');
    } catch (error) {
      toast.error('Could not copy the local settings link');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="mt-16">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (loadError || !payload?.agent) {
    return (
      <AdminLayout>
        <h1 className="text-2xl font-semibold text-gray-900">
          {loadError ? 'Could not load this computer' : 'Computer not found'}
        </h1>
        {loadError ? <p className="mt-2 max-w-xl text-sm text-gray-500">{loadError}</p> : null}
        <Link to={`/discovery/${sessionId}`} className="mt-4 inline-block text-sm font-medium text-primary-700">
          Back to session
        </Link>
      </AdminLayout>
    );
  }

  const { agent, exclusions, health } = payload;
  const keptShareValue = health ? Number.parseFloat(health.keptShare) : Number.NaN;
  const metrics = health
    ? [
        {
          label: 'Cards kept',
          value: String(health.cardsKept),
          sub: 'Windows recorded and uploaded as cards',
          Icon: CheckCircleIcon,
          tile: 'bg-emerald-50 text-emerald-600 ring-emerald-600/15',
          accent: 'bg-emerald-500',
        },
        {
          label: 'Dropped',
          value: String(health.windowsDropped),
          sub: 'Personal, excluded, or under the 70% bar',
          Icon: EyeSlashIcon,
          tile: 'bg-amber-50 text-amber-600 ring-amber-600/20',
          accent: 'bg-amber-400',
        },
        {
          label: 'Unreadable',
          value: String(health.windowsUnreadable),
          sub: 'Windows the vision pass could not read',
          Icon: ExclamationCircleIcon,
          tile: 'bg-red-50 text-red-600 ring-red-600/15',
          accent: 'bg-red-500',
        },
        {
          label: 'Kept share',
          value: health.keptShare,
          sub: 'Of decided windows, the share that was kept',
          Icon: ChartBarIcon,
          tile: 'bg-primary-50 text-primary-600 ring-primary-600/15',
          accent: 'bg-primary-500',
          bar: Number.isFinite(keptShareValue) ? Math.min(100, Math.max(0, keptShareValue)) : null,
        },
        {
          label: 'Est. cost',
          value: health.estimatedCost || '$0',
          sub: health.estimatedCostNote || 'Claude vision reads',
          title: health.estimatedCostDetail || health.estimatedCostNote || 'Estimated Claude vision spend',
          Icon: CurrencyDollarIcon,
          tile: 'bg-sky-50 text-sky-700 ring-sky-600/15',
          accent: 'bg-sky-500',
        },
      ]
    : [];

  const selectedIndex = events.findIndex((event) => event.id === selectedId);
  const selectedEvent = selectedIndex >= 0 ? events[selectedIndex] : null;

  return (
    <AdminLayout>
      <Link to={`/discovery/${sessionId}`} className="text-sm font-medium text-primary-700 hover:text-primary-800">
        Back to session
      </Link>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{agent.machineName}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {agent.role} · {agent.os} · last check-in {health?.ago || agent.lastCheckInLabel}
            {health?.lastCheckIn ? ` · ${health.lastCheckIn} Israel time` : ''}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {(exclusions || []).length
              ? `Excluded applications: ${exclusions.join(', ')}`
              : 'Recording every application in front.'}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {agent.workingHours
              ? `Own hours · ${describeWorkingHours(agent.workingHours)}`
              : `Session hours · ${describeWorkingHours(payload.sessionWorkingHours)}`}
          </p>
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={LOCAL_SETTINGS_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 items-center rounded-md bg-primary-600 px-3 text-sm font-medium text-white hover:bg-primary-700"
            >
              Local settings
            </a>
            <button
              type="button"
              onClick={copyLocalSettings}
              className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              Copy
            </button>
            <button
              type="button"
              onClick={() => { setConfirmClear(true); setConfirmDelete(false); }}
              className="inline-flex h-9 items-center rounded-md border border-amber-300 bg-white px-3 text-sm font-medium text-amber-800 hover:bg-amber-50"
            >
              Clear data
            </button>
            <button
              type="button"
              onClick={() => { setConfirmDelete(true); setConfirmClear(false); }}
              className="inline-flex h-9 items-center rounded-md border border-red-200 bg-white px-3 text-sm font-medium text-red-700 hover:bg-red-50"
            >
              Delete
            </button>
            {agent.captureState === 'stopped' && payload.sessionStatus === 'active' ? (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await axios.post(
                      `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/agents/${agentId}`,
                      { captureState: 'on' },
                    );
                    toast.success('Capture resumed');
                    await load();
                  } catch (error) {
                    toast.error(error.response?.data?.error || 'Capture could not be resumed.');
                  }
                }}
                className="inline-flex h-9 items-center rounded-md bg-primary-600 px-3 text-sm font-medium text-white hover:bg-primary-700"
              >
                Resume
              </button>
            ) : null}
            <span className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass[agent.captureState] || statusClass.stopped}`}>
              {agent.captureState}
            </span>
          </div>
          <p className="text-xs text-gray-500">On the computer running this agent · {LOCAL_SETTINGS_URL}</p>
        </div>
      </div>

      {confirmDelete ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="max-w-xl text-sm text-red-900">
            Delete {agent.machineName}? Its {agent.cardsSent} cards, frames, and event log are removed, and the living
            document is rebuilt without them. A computer still running the observer will check in again and reappear.
          </p>
          <span className="flex gap-2">
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              disabled={deleting}
              onClick={() => setConfirmDelete(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-md bg-red-700 px-3 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await axios.delete(
                    `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/agents/${agentId}`,
                  );
                  toast.success('Computer deleted');
                  navigate(`/discovery/${sessionId}`);
                } catch (error) {
                  toast.error(error.response?.data?.error || 'The computer could not be deleted.');
                  setDeleting(false);
                }
              }}
            >
              {deleting ? 'Deleting…' : 'Delete computer'}
            </button>
          </span>
        </div>
      ) : null}

      {confirmClear ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <p className="max-w-xl text-sm text-amber-900">
            Clear all data collected from {agent.machineName}? Its {agent.cardsSent} cards, their frames, and its
            event log are removed, and the living document is rebuilt without them. The computer stays enrolled
            and keeps reporting from an empty slate.
          </p>
          <span className="flex gap-2">
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              disabled={clearing}
              onClick={() => setConfirmClear(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-md bg-amber-600 px-3 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              disabled={clearing}
              onClick={async () => {
                setClearing(true);
                try {
                  const response = await axios.post(
                    `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/agents/${agentId}`,
                    { action: 'clear-data' },
                  );
                  const cleared = response.data?.data?.cleared || response.data?.cleared || {};
                  toast.success(
                    `Cleared ${cleared.cards ?? agent.cardsSent} cards and ${cleared.events ?? 0} log events from ${agent.machineName}.`,
                  );
                  setConfirmClear(false);
                  setSelectedId(null);
                  setPage(1);
                  await load();
                } catch (error) {
                  toast.error(error.response?.data?.error || 'The data could not be cleared.');
                } finally {
                  setClearing(false);
                }
              }}
            >
              {clearing ? 'Clearing…' : 'Clear all data'}
            </button>
          </span>
        </div>
      ) : null}

      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-medium text-gray-900">Working hours</h2>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          {hoursMode === 'session'
            ? `This computer follows the session. ${describeWorkingHours(payload.sessionWorkingHours)}. Outside those hours it does not record.`
            : 'These hours replace the session for this computer only. Outside them, this computer does not record.'}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              hoursDirtyRef.current = true;
              setHoursMode('session');
            }}
            className={`inline-flex h-9 items-center rounded-md px-3 text-sm font-medium ${
              hoursMode === 'session'
                ? 'bg-primary-600 text-white'
                : 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
            }`}
          >
            Follow the session
          </button>
          <button
            type="button"
            onClick={() => {
              hoursDirtyRef.current = true;
              setHoursMode('custom');
              if (!payload.agent.workingHours) setHours(normalizeWorkingHours(payload.sessionWorkingHours));
            }}
            className={`inline-flex h-9 items-center rounded-md px-3 text-sm font-medium ${
              hoursMode === 'custom'
                ? 'bg-primary-600 text-white'
                : 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
            }`}
          >
            Custom for this computer
          </button>
        </div>
        {hoursMode === 'custom' ? (
          <div className="mt-4 max-w-xl">
            <WorkingHoursFields
              value={hours}
              onChange={(next) => {
                hoursDirtyRef.current = true;
                setHours(next);
              }}
              idPrefix="agent-hours"
            />
          </div>
        ) : null}
        <button
          type="button"
          disabled={savingHours || (hoursMode === 'custom' && Boolean(workingHoursProblem(hours)))}
          onClick={async () => {
            const problem = hoursMode === 'custom' ? workingHoursProblem(hours) : '';
            if (problem) {
              toast.error(problem);
              return;
            }
            setSavingHours(true);
            try {
              await axios.post(
                `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/agents/${agentId}`,
                { workingHours: hoursMode === 'session' ? null : hours },
              );
              hoursDirtyRef.current = false;
              toast.success(hoursMode === 'session' ? 'This computer follows the session hours' : 'Working hours saved');
              await load();
            } catch (error) {
              toast.error(error.response?.data?.error || 'The working hours could not be saved.');
            } finally {
              setSavingHours(false);
            }
          }}
          className="mt-4 inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          {savingHours ? 'Saving…' : 'Save hours'}
        </button>
      </section>

      {health ? (
        <section className="mt-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${toneClass[health.tone] || toneClass.stopped}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${health.tone === 'healthy' ? 'animate-pulse bg-emerald-500' : 'bg-current opacity-60'}`} />
              {health.label}
            </span>
            <p className="text-sm text-gray-600">{health.summary}</p>
          </div>
          <dl className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {metrics.map(({ label, value, sub, title, Icon, tile, accent, bar }) => (
              <div
                key={label}
                title={title || sub}
                className="relative overflow-hidden rounded-lg border border-gray-200 bg-white py-2.5 pl-4 pr-3 shadow-sm transition-shadow hover:shadow"
              >
                <span className={`absolute inset-y-0 left-0 w-0.5 ${accent}`} aria-hidden="true" />
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
                  <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md ring-1 ring-inset ${tile}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                </div>
                <dd className="mt-0.5 flex items-baseline gap-2">
                  <span className="text-xl font-bold leading-none tracking-tight text-gray-900">{value}</span>
                  <span className="min-w-0 truncate text-[11px] leading-none text-gray-500">{sub}</span>
                </dd>
                {bar !== null && bar !== undefined ? (
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-100" role="img" aria-label={`Kept share ${bar}%`}>
                    <div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${bar}%` }} />
                  </div>
                ) : null}
              </div>
            ))}
          </dl>
          <form
            className="mt-3 flex flex-wrap items-end gap-2"
            onSubmit={async (event) => {
              event.preventDefault();
              setSavingLimit(true);
              try {
                const response = await axios.post(
                  `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/agents/${agentId}`,
                  { costLimit: costLimit.trim() ? costLimit.trim() : null },
                );
                limitDirtyRef.current = false;
                const stopped = response.data?.data?.stopped || response.data?.stopped;
                toast.success(stopped ? 'Cost limit saved. This computer is stopped.' : 'Cost limit saved');
                await load();
              } catch (error) {
                toast.error(error.response?.data?.error || 'The cost limit could not be saved.');
              } finally {
                setSavingLimit(false);
              }
            }}
          >
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Cost limit USD</span>
              <input
                value={costLimit}
                onChange={(event) => {
                  limitDirtyRef.current = true;
                  setCostLimit(event.target.value);
                }}
                inputMode="decimal"
                placeholder="No limit"
                aria-label="Cost limit in US dollars"
                className="mt-1 h-9 w-28 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900"
              />
            </label>
            <button
              type="submit"
              disabled={savingLimit}
              className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              {savingLimit ? 'Saving…' : 'Save limit'}
            </button>
            {health.costLimit ? (
              <button
                type="button"
                disabled={savingLimit}
                onClick={async () => {
                  setSavingLimit(true);
                  try {
                    await axios.post(
                      `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/agents/${agentId}`,
                      { costLimit: null },
                    );
                    limitDirtyRef.current = false;
                    setCostLimit('');
                    toast.success('Cost limit cleared');
                    await load();
                  } catch (error) {
                    toast.error(error.response?.data?.error || 'The cost limit could not be cleared.');
                  } finally {
                    setSavingLimit(false);
                  }
                }}
                className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                Clear
              </button>
            ) : null}
            <p className="min-w-[16rem] flex-1 text-[11px] leading-snug text-gray-500">
              {health.costLimitReached
                ? `Stopped at the ${health.costLimit} limit. Raise it, then resume capture.`
                : 'When the estimate reaches this amount, this computer stops on its next check-in.'}
            </p>
          </form>
        </section>
      ) : null}

      <section ref={logRef} className="mt-8 scroll-mt-4">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Agent log</h2>
            <p className="mt-1 text-sm text-gray-500">
              The first page follows this computer. Search and filters cover the retained log. Click any row for the full vision details.
            </p>
          </div>
          <LogPager page={page} pages={pages} total={total} onPage={goToPage} following={live && page === 1} onLive={resumeLive} />
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {/* Toolbar, PostHog Logs style */}
          <div className="flex flex-col gap-2 border-b border-gray-200 p-2 lg:flex-row lg:items-center">
            <label className="relative block lg:flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search application or window"
                aria-label="Search the log"
                className="h-9 w-full rounded-md border border-gray-300 bg-white pl-8 pr-3 text-sm text-gray-900"
              />
            </label>
            <select
              value={kind}
              onChange={(event) => { setKind(event.target.value); setPage(1); }}
              aria-label="Filter by activity"
              className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900"
            >
              <option value="">All activity</option>
              <option value="uploaded">Cards kept</option>
              <option value="dropped">Dropped windows</option>
              <option value="unread">Unreadable windows</option>
            </select>
            <select
              value={app}
              onChange={(event) => { setApp(event.target.value); setPage(1); }}
              aria-label="Filter by application"
              className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900"
            >
              <option value="">All applications</option>
              {apps.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {/* Volume over the loaded page */}
          {events.length > 0 ? (
            <div className="border-b border-gray-200 px-3 pb-2 pt-3">
              <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-gray-400">
                <span>Volume over time</span>
                <span>{total} retained</span>
              </div>
              <div className="mt-2 flex h-12 items-end gap-[2px]" aria-hidden="true">
                {volume.buckets.map((bucket, index) => (
                  <div
                    key={index}
                    className="flex min-w-[3px] flex-1 flex-col-reverse overflow-hidden rounded-t-sm bg-gray-100"
                    style={{ height: '100%' }}
                    title={bucket.at ? `${bucket.total} events · ${new Date(bucket.at).toLocaleString()}` : 'No events'}
                  >
                    {['error', 'warn', 'info'].map((level) => bucket[level] > 0 ? (
                      <div key={level} className={levelMeta[Object.keys(levelMeta).find((key) => levelMeta[key].level === level)]?.bar || 'bg-gray-400'} style={{ height: `${(bucket[level] / volume.max) * 100}%` }} />
                    ) : null)}
                    {bucket.total === 0 ? <div className="bg-gray-100" style={{ height: '8%' }} /> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex items-stretch">
            {/* Facets */}
            <aside className="hidden w-52 shrink-0 border-r border-gray-200 bg-gray-50/60 p-3 md:block">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Level</p>
              <div className="mt-2 space-y-1">
                {Object.entries(levelMeta).map(([kindKey, meta]) => {
                  const active = kind === kindKey;
                  const count = kindCounts ? kindCounts[kindKey] : null;
                  return (
                    <button
                      key={kindKey}
                      type="button"
                      onClick={() => { setKind(active ? '' : kindKey); setPage(1); }}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] ${active ? 'bg-primary-50 font-medium text-primary-800 ring-1 ring-inset ring-primary-200' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      <span className={`h-2 w-2 shrink-0 rounded-full ${meta.dot}`} />
                      <span className="flex-1">{meta.label}</span>
                      <span className="text-xs text-gray-400">{count === null || count === undefined ? kindLabel[kindKey] : count}</span>
                    </button>
                  );
                })}
              </div>
              {Array.isArray(payload.rulings) && payload.rulings.length > 0 ? (
                <>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Taught to Jev</p>
                  <div className="mt-2 max-h-40 space-y-1 overflow-y-auto pr-1">
                    {payload.rulings.map((ruling) => (
                      <div key={ruling.id} className="rounded-md px-2 py-1.5 text-[13px] text-gray-700">
                        <div className="flex items-start gap-2">
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-gray-800">{ruling.app}</span>
                            <span className="block truncate text-xs text-gray-500">
                              {ruling.windowTitle ? `${ruling.windowTitle} · ` : ''}
                              {ruling.label === 'personal' ? 'Personal use' : 'Business work'}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              forgetRuling(sessionId, ruling.id)
                                .then(() => {
                                  toast.success('Jev will classify this kind of window again.');
                                  return load();
                                })
                                .catch((error) => {
                                  toast.error(error.response?.data?.error || 'Could not forget this correction.');
                                });
                            }}
                            className="shrink-0 text-xs font-medium text-gray-500 hover:text-gray-800"
                          >
                            Forget
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
              <p className="mt-5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Application</p>
              <div className="mt-2 max-h-72 space-y-1 overflow-y-auto pr-1">
                {apps.length === 0 ? <p className="px-2 text-[13px] text-gray-400">No applications yet</p> : apps.map((name) => {
                  const active = app === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => { setApp(active ? '' : name); setPage(1); }}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] ${active ? 'bg-primary-50 font-medium text-primary-800 ring-1 ring-inset ring-primary-200' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      <AppIcon app={name} icon={appIcons[name]} size="sm" />
                      <span className="min-w-0 flex-1 truncate">{name}</span>
                      <span className="text-xs text-gray-400">{appCounts.get(name) || ''}</span>
                    </button>
                  );
                })}
              </div>
              {(kind || app || search) ? (
                <button
                  type="button"
                  onClick={() => { setKind(''); setApp(''); setQuery(''); setPage(1); }}
                  className="mt-4 text-[13px] font-medium text-primary-700 hover:text-primary-800"
                >
                  Clear all filters
                </button>
              ) : null}
            </aside>

            {/* Log table */}
            <div className="min-w-0 flex-1 overflow-x-auto">
              {events.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">
                  {search || kind || app
                    ? 'Nothing in the retained log matches these filters.'
                    : 'No activity yet. The first kept or dropped window writes the first line.'}
                </div>
              ) : (
                <table className="w-full min-w-[720px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      <th className="w-44 px-3 py-2">Timestamp</th>
                      <th className="w-24 px-3 py-2">Level</th>
                      <th className="w-52 px-3 py-2">Application</th>
                      <th className="px-3 py-2">Message</th>
                      <th className="w-8 px-2 py-2" aria-label="Open details" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {events.map((event) => {
                      const described = describeEvent(event);
                      const meta = levelMeta[event.kind] || levelMeta.uploaded;
                      const selected = event.id === selectedId;
                      return (
                        <tr
                          key={event.id}
                          tabIndex={0}
                          onClick={() => setSelectedId(event.id)}
                          onKeyDown={(keyEvent) => { if (keyEvent.key === 'Enter' || keyEvent.key === ' ') { keyEvent.preventDefault(); setSelectedId(event.id); } }}
                          className={`cursor-pointer align-top outline-none transition-colors hover:bg-primary-50/50 focus:bg-primary-50 ${selected ? 'bg-primary-50 ring-1 ring-inset ring-primary-200' : ''} ${freshIds.has(event.id) ? 'bg-green-50' : ''}`}
                          aria-label={`${described.headline}: ${described.message}`}
                        >
                          <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-gray-500">{event.at}</td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.badge}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="flex min-w-0 items-center gap-2">
                              <AppIcon app={event.app} icon={event.appIcon || appIcons[event.app]} />
                              <span className="truncate text-[13px] font-medium text-gray-800">{event.app || 'Unknown app'}</span>
                            </span>
                          </td>
                          <td className="max-w-0 px-3 py-2.5">
                            <span className="block truncate text-[13px] text-gray-800">
                              <span className="font-semibold text-gray-900">{described.headline}</span>
                              {described.message && described.message !== described.headline ? <span className="text-gray-600"> — {described.message}</span> : null}
                            </span>
                            <span className="mt-1 flex flex-wrap gap-1">
                              {described.window ? <Chip icon={EyeIcon}>{described.window}</Chip> : null}
                              {described.medium ? <Chip icon={described.medium === 'clip' ? VideoCameraIcon : CameraIcon}>{described.medium === 'clip' ? 'Clip' : 'Still'}</Chip> : null}
                              {described.durationLabel ? <Chip icon={ClockIcon}>{described.durationLabel} on window</Chip> : null}
                              {described.confidence !== null && described.confidence !== undefined ? <Chip icon={ExclamationTriangleIcon}>Jev {described.confidence}% sure</Chip> : null}
                              {event.ruling ? <Chip icon={event.ruling.label === 'personal' ? EyeSlashIcon : CheckCircleIcon}>{event.ruling.label === 'personal' ? 'Marked personal' : 'Marked business work'}</Chip> : null}
                              {described.changed ? <Chip>Changed: {described.changed}</Chip> : null}
                            </span>
                          </td>
                          <td className="px-2 py-2.5 text-gray-400"><ChevronRightIcon className="h-4 w-4" /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {events.length > 0 ? (
          <div className="flex justify-end">
            <LogPager page={page} pages={pages} total={total} onPage={goToPage} following={live && page === 1} onLive={resumeLive} />
          </div>
        ) : null}
      </section>

      {selectedEvent ? createPortal(
        <LogDrawer
          key={selectedEvent.id}
          event={selectedEvent}
          sessionId={sessionId}
          agentId={agentId}
          onChanged={load}
          onClose={() => setSelectedId(null)}
          onNewer={selectedIndex > 0 ? () => setSelectedId(events[selectedIndex - 1].id) : null}
          onOlder={selectedIndex < events.length - 1 ? () => setSelectedId(events[selectedIndex + 1].id) : null}
        />,
        document.body,
      ) : null}
    </AdminLayout>
  );
};

function AppIcon({ app, icon, size = 'md' }) {
  const meta = appMeta(app);
  const box = size === 'sm' ? 'h-6 w-6 rounded' : 'h-7 w-7 rounded-md';
  const glyph = size === 'sm' ? 'h-3.5 w-3.5' : 'h-[18px] w-[18px]';
  // A web app's own favicon, picked up by the observer from the page itself,
  // beats a bundled brand glyph or a letter tile.
  if (icon) {
    return (
      <span className={`inline-flex ${box} shrink-0 items-center justify-center bg-white ring-1 ring-inset ring-gray-200`} title={meta.name}>
        <img src={icon} alt={meta.name} className={`${glyph} object-contain`} draggable="false" />
      </span>
    );
  }
  if (meta.icon) {
    return (
      <span className={`inline-flex ${box} shrink-0 items-center justify-center bg-white ring-1 ring-inset ring-gray-200`} title={meta.name}>
        <img src={meta.icon} alt={meta.name} className={`${glyph} object-contain`} draggable="false" />
      </span>
    );
  }
  return (
    <span className={`inline-flex ${box} shrink-0 items-center justify-center text-white shadow-sm`} style={{ backgroundColor: meta.color }} title={meta.name} aria-hidden="true">
      <span className="text-[11px] font-bold">{meta.initial || '?'}</span>
    </span>
  );
}

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
      {Icon ? <Icon className="h-3 w-3 shrink-0 text-gray-400" /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
}

function timestampRows(event) {
  const date = new Date(event.atIso || '');
  if (Number.isNaN(date.getTime())) return [['Displayed', event.at || '—']];
  return [
    ['Displayed (Israel)', event.at || date.toLocaleString('en-GB', { timeZone: 'Asia/Jerusalem' })],
    ['Your device', date.toLocaleString('en-GB')],
    ['UTC', `${date.toISOString().replace('T', ' ').slice(0, 19)} UTC`],
    ['UNIX', String(Math.floor(date.getTime() / 1000))],
  ];
}

function ClassificationControl({ sessionId, agentId, event, onChanged }) {
  const ruling = event.ruling || null;
  const [note, setNote] = useState(ruling?.note || '');
  const [saving, setSaving] = useState('');
  useEffect(() => {
    setNote(ruling?.note || '');
  }, [event.id, ruling?.id, ruling?.label, ruling?.note]);
  if (!event.correctable && !ruling) return null;

  const save = async (label) => {
    setSaving(label);
    try {
      const saved = await saveRuling(sessionId, agentId, event.id, label, note);
      toast.success(saved?.applies || 'Saved. The next window of this kind follows this correction.');
      if (onChanged) await onChanged();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not save this correction.');
    } finally {
      setSaving('');
    }
  };
  const forget = async () => {
    if (!ruling) return;
    setSaving('forget');
    try {
      await forgetRuling(sessionId, ruling.id);
      toast.success('Jev will classify this kind of window again.');
      if (onChanged) await onChanged();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not forget this correction.');
    } finally {
      setSaving('');
    }
  };

  const choice = (label, text) => {
    const selected = ruling?.label === label;
    return (
      <button
        type="button"
        disabled={Boolean(saving)}
        onClick={() => save(label)}
        className={`inline-flex h-9 items-center rounded-md px-3 text-sm font-medium disabled:opacity-50 ${
          selected
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
        }`}
      >
        {saving === label ? 'Saving…' : text}
      </button>
    );
  };

  return (
    <section>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Correct this classification</h4>
      <p className="mt-2 text-sm leading-6 text-gray-600">
        {ruling
          ? `Saved as ${ruling.label === 'personal' ? 'personal use' : 'business work'}${ruling.at ? ` · ${ruling.at}` : ''}. The next window like this follows it, and Jev receives it as an example.`
          : 'If Jev got this window wrong, mark what it should have been. The next window like this follows your decision, and Jev receives it as an example. A window already dropped cannot be added back.'}
      </p>
      <label className="mt-3 block text-[13px] font-medium text-gray-700" htmlFor={`ruling-note-${event.id}`}>
        Note for Jev
      </label>
      <textarea
        id={`ruling-note-${event.id}`}
        value={note}
        maxLength={240}
        rows={2}
        onChange={(change) => setNote(change.target.value)}
        placeholder="Why this is business work or personal use"
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
      />
      <p className="mt-1 text-xs leading-5 text-gray-500">
        A chat app needs this note. Jev learns the kind of conversation from it. The app itself is not kept or dropped as a whole.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {choice('professional', ruling?.label === 'professional' ? 'Business work' : 'This is business work')}
        {choice('personal', ruling?.label === 'personal' ? 'Personal use' : 'This is personal use')}
        {ruling ? (
          <button
            type="button"
            disabled={Boolean(saving)}
            onClick={forget}
            className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50"
          >
            {saving === 'forget' ? 'Forgetting…' : 'Forget'}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function LogDrawer({ event, sessionId, agentId, onChanged, onClose, onNewer, onOlder }) {
  const [tab, setTab] = useState('details');
  const described = describeEvent(event);
  const meta = levelMeta[event.kind] || levelMeta.uploaded;
  const hasVision = Boolean(described.window || described.screen || described.changed || described.medium || described.durationLabel);
  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ ...event, parsed: described }, null, 2));
      toast.success('Log line copied');
    } catch (error) {
      toast.error('Could not copy this log line');
    }
  };
  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Log details">
      <button type="button" aria-label="Close log details" className="absolute inset-0 h-full w-full cursor-default bg-gray-950/40" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 flex w-[520px] max-w-[94vw] flex-col bg-white shadow-2xl">
        <header className="border-b border-gray-200 px-5 py-4">
          <div className="flex items-start gap-3">
            <AppIcon app={event.app} icon={event.appIcon} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">{described.headline}</h3>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.badge}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />{meta.label}
                </span>
              </div>
              <p className="mt-0.5 truncate text-sm text-gray-500">{event.app || 'Unknown app'} · {event.at}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button type="button" onClick={onNewer} disabled={!onNewer} title="Newer log line" className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"><ArrowLeftIcon className="h-4 w-4" /></button>
              <button type="button" onClick={onOlder} disabled={!onOlder} title="Older log line" className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"><ArrowRightIcon className="h-4 w-4" /></button>
              <button type="button" onClick={onClose} title="Close (Esc)" className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"><XMarkIcon className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="mt-4 flex gap-5 border-b border-transparent text-sm">
            {[['details', 'Details'], ['raw', 'Raw']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={`-mb-px border-b-2 pb-2 font-medium ${tab === value ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {tab === 'raw' ? (
            <pre className="overflow-x-auto rounded-lg bg-gray-950 p-4 font-mono text-xs leading-5 text-gray-100">{JSON.stringify({ ...event, parsed: described }, null, 2)}</pre>
          ) : (
            <div className="space-y-6">
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Log message</h4>
                <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2.5 text-sm leading-6 text-gray-800">{described.message}</p>
                {event.detail && event.detail !== described.message ? (
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-gray-600">{event.detail}</p>
                ) : null}
              </section>

              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Timestamp conversion</h4>
                <dl className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {timestampRows(event).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-4 px-3 py-2 text-[13px]">
                      <dt className="text-gray-500">{label}</dt>
                      <dd className="font-mono text-xs text-gray-800">{value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              {hasVision ? (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">What the vision pass saw</h4>
                  <dl className="mt-2 grid grid-cols-2 gap-2">
                    {[
                      ['Window', described.window], ['Screen', described.screen], ['Changed', described.changed],
                      ['Recorded as', described.medium === 'still' ? 'Still image' : described.medium === 'clip' ? 'Clip' : ''],
                      ['Time on window', described.durationLabel], ['Action', described.action],
                    ].filter(([, value]) => value).map(([label, value]) => (
                      <div key={label} className="rounded-lg bg-gray-50 px-3 py-2.5">
                        <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</dt>
                        <dd className="mt-1 break-words text-[13px] font-medium leading-5 text-gray-800">{value}</dd>
                      </div>
                    ))}
                  </dl>
                  {described.reason ? (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-amber-600">Why it was dropped</p>
                      <p className="mt-1 text-[13px] leading-5 text-amber-900">{described.reason}</p>
                      {described.confidence !== null && described.confidence !== undefined ? (
                        <div className="mt-2">
                          <div className="flex justify-between text-[11px] font-medium text-amber-700"><span>Jev confidence</span><span>{described.confidence}% · needs 70%</span></div>
                          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-amber-200/70">
                            <div className="h-full rounded-full bg-amber-500" style={{ width: `${described.confidence}%` }} />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              ) : described.reason ? (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Reason</h4>
                  <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2.5 text-sm leading-6 text-gray-700">{described.reason}</p>
                </section>
              ) : null}

              <ClassificationControl sessionId={sessionId} agentId={agentId} event={event} onChanged={onChanged} />

              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Log attributes</h4>
                <dl className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {[
                    { key: 'kind', label: 'Kind', value: event.kind },
                    { key: 'app', label: 'Application', value: event.app || '—' },
                    { key: 'event.id', label: 'Event ID', value: event.id },
                    ...described.attributes,
                  ].map((attribute) => (
                    <div key={attribute.key} className="grid grid-cols-[150px_1fr] gap-3 px-3 py-2 text-[13px]">
                      <dt className="truncate font-mono text-xs leading-5 text-gray-500" title={attribute.key}>{attribute.key}</dt>
                      <dd className="break-words leading-5 text-gray-800">{attribute.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">
          <p className="truncate font-mono text-[11px] text-gray-400">{event.id}</p>
          <button type="button" onClick={copyJson} className="inline-flex h-9 shrink-0 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50">
            Copy JSON
          </button>
        </footer>
      </aside>
    </div>
  );
}

function LogPager({ page, pages, total, onPage, following, onLive }) {
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(total, page * PAGE_SIZE);
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onLive}
        className={`inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium ${
          following
            ? 'bg-green-50 text-green-800 ring-1 ring-inset ring-green-600/20'
            : 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
        }`}
      >
        <span className={`h-2 w-2 rounded-full ${following ? 'animate-pulse bg-green-500' : 'bg-gray-300'}`} />
        {following ? 'Live' : 'Live tail'}
      </button>
      <button
        type="button"
        className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40"
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
      >
        Previous
      </button>
      <button
        type="button"
        className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40"
        onClick={() => onPage(page + 1)}
        disabled={page >= pages}
      >
        Next
      </button>
      <p className="text-sm text-gray-600">
        {following ? 'Following the latest' : `Page ${page} of ${pages}`}
        <span className="text-gray-400"> · </span>
        {start}–{end} of {total}
      </p>
    </div>
  );
}

export default DiscoveryAgent;
